///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2023 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../helpers/messages";
import HeaderCell from "../../HeaderCell";
import {
  clearRowsSelected,
  filterCapacityMatchesSummary,
  setAllRowsSelected,
  setColumnOrder,
  setColumnWidth,
  sortCapacityMatchLaneSummary,
  toggleColumnFilterMenu
} from "../../../slices/bidProcurementSlice";
import getSortOrder from "../../../helpers/getSortOrder";
import FilterMenu from "../../form-controls/FilterMenu";
import capacityMatchLaneSummaryColumns from "../../../helpers/capacityMatchLaneSummaryColumns";

const CapacityMatchTableHeader = ({
  isPendingOrRejected,
  isSubmitted,
  isReviewForCarrierReps,
  isCapacityMatchingInProgress
}) => {
  const dispatch = useDispatch();
  const {
    bidProcurement: {
      capacityMatchLaneSummary: {
        isFetching,
        sortField,
        sortOrder,
        columnWidths,
        capacityMatches,
        columnFilterMenu,
        columnFilters,
        rangeFilters,
        columns: { order: columnOrder }
      },
      rowsSelected,
      copyMatchesFromRow
    },
    featureFlags: { enableDragAndDropForIcm }
  } = useSelector(state => state);

  const sortCapacityMatches = ({ fieldName }) => {
    const field =
      fieldName === "groups" ||
      fieldName === "carrierNames" ||
      fieldName === "repNames" ||
      fieldName === "procurementRepNames"
        ? fieldName.concat("Count")
        : fieldName;

    const order = getSortOrder(field, sortField, sortOrder);
    const sortParams = {
      field: order ? field : "",
      order: order
    };
    dispatch(sortCapacityMatchLaneSummary(sortParams));
  };

  const commonHeaderProps = (columnName, className, sortEnabled) => ({
    sortBids: sortEnabled ? sortCapacityMatches : () => {},
    sortField,
    sortOrder,
    isFetching,
    resize: true,
    columnWidth: columnWidths[columnName] || "",
    setColumnWidth: width =>
      dispatch(setColumnWidth({ field: columnName, width })),
    className: `m-0 p-0 align-middle ${className}`
  });

  const checked =
    rowsSelected.length &&
    capacityMatches?.length &&
    (rowsSelected.length === capacityMatches?.length ||
      (copyMatchesFromRow &&
        rowsSelected.length + 1 === capacityMatches?.length));

  const dropIndicatorClassName = "drop-indicator";

  const handleDragStart = e => {
    e.dataTransfer.setData("pick", e.target.id);
  };

  const removeDropIndicator = () => {
    while (document.getElementsByClassName(dropIndicatorClassName).length) {
      document
        .getElementsByClassName(dropIndicatorClassName)[0]
        .classList.remove(dropIndicatorClassName);
    }
  };

  const onDragOver = (e, fieldName) => {
    e.preventDefault();
    if (fieldName) {
      removeDropIndicator();
      document
        .querySelector(`#capacity-match-grid #${fieldName}`)
        .classList.add(dropIndicatorClassName);
    }
  };

  const handleDrop = (e, drop) => {
    const pick = e.dataTransfer.getData("pick");
    if (drop && columnOrder.some(c => c === pick)) {
      e.stopPropagation();
      dispatch(setColumnOrder({ pick, drop }));
    } else {
      removeDropIndicator();
    }
  };

  const Filter = ({ fieldName }) => {
    const rangeFilter = fieldName === "volume";
    const multiValuedColumn =
      fieldName === "groups" ||
      fieldName === "carrierNames" ||
      fieldName === "repNames" ||
      fieldName === "procurementRepNames";
    const rangeFilterData = rangeFilters[fieldName];

    return (
      <span className="fr mr-8">
        <FilterMenu
          id="pricing-grid-filter-menu"
          disabled={false}
          appendTo={document.body}
          offset={[0, 8]}
          showApplyButton={true}
          isSearchable={true}
          field={fieldName}
          options={columnFilters[fieldName] || []}
          handleFilter={(items, field) => {
            dispatch(
              filterCapacityMatchesSummary({
                items,
                field
              })
            );
          }}
          show={() =>
            dispatch(
              toggleColumnFilterMenu({
                field: fieldName,
                visible: true,
                filter: rangeFilter ? false : true,
                multiValuedColumn
              })
            )
          }
          visible={
            columnFilterMenu.field === fieldName && columnFilterMenu.visible
          }
          onClickOutside={() =>
            dispatch(
              toggleColumnFilterMenu({
                field: fieldName,
                visible: false,
                filter: rangeFilter ? false : true,
                multiValuedColumn
              })
            )
          }
          rangeFilter={rangeFilter}
          handleRangeFilter={(range, field) => {
            dispatch(
              filterCapacityMatchesSummary({
                field,
                range
              })
            );
          }}
          rangeFilterApplied={rangeFilterData}
        />
      </span>
    );
  };

  const CustomHeaderCell = ({
    fieldName,
    label,
    checkbox,
    minWidth,
    maxWidth,
    isDraggable,
    isFilter,
    sortEnabled,
    left,
    right,
    checkCapacityStatusForSorting,
    resize,
    hideLabel
  }) => {
    const commonHeaderProps = (columnName, sortEnabled, resizable) => ({
      sortBids: sortEnabled ? sortCapacityMatches : () => {},
      sortField,
      sortOrder,
      columnWidth: columnWidths[columnName] || "",
      setColumnWidth: resizable
        ? width => dispatch(setColumnWidth({ field: columnName, width }))
        : null
    });

    return (
      <HeaderCell
        {...commonHeaderProps(
          fieldName,
          checkCapacityStatusForSorting && sortEnabled
            ? !isPendingOrRejected
            : sortEnabled,
          resize
        )}
        fieldName={fieldName}
        isFetching={isFetching}
        checkbox={checkbox}
        className={`m-0 p-0 align-middle ${left ? "left" : ""} ${
          right ? "right" : ""
        }`}
        maxWidth={maxWidth}
        checkboxDisabled={
          isPendingOrRejected ||
          isSubmitted ||
          isReviewForCarrierReps ||
          isCapacityMatchingInProgress
        }
        showIndeterminateCheckbox={
          rowsSelected.length &&
          capacityMatches?.length &&
          (rowsSelected.length < capacityMatches?.length || copyMatchesFromRow)
        }
        checked={checked}
        handleCheckboxClick={() =>
          checked
            ? dispatch(clearRowsSelected())
            : dispatch(setAllRowsSelected())
        }
        label={label}
        minWidth={minWidth}
        isFilter={isFilter}
        filter={Filter}
        resize={resize}
        hideLabel={hideLabel}
        draggable={isDraggable}
        onDragStart={handleDragStart}
        onDragEnd={() => removeDropIndicator()}
        onDrop={e => handleDrop(e, isDraggable === false ? false : fieldName)}
        onDragOver={e => onDragOver(e, fieldName)}
      />
    );
  };

  return (
    <thead data-testid="capacity-match-table-header">
      <tr className="header-row bg-white">
        {enableDragAndDropForIcm ? (
          columnOrder.map((c, index) => (
            <CustomHeaderCell
              key={index}
              {...capacityMatchLaneSummaryColumns.find(
                ({ fieldName }) => fieldName === c
              )}
            />
          ))
        ) : (
          <>
            <HeaderCell
              fieldName={""}
              isFetching={isFetching}
              checkbox={true}
              className="m-0 p-0 align-middle"
              maxWidth={50}
              checkboxDisabled={
                isPendingOrRejected ||
                isSubmitted ||
                isReviewForCarrierReps ||
                isCapacityMatchingInProgress
              }
              showIndeterminateCheckbox={
                rowsSelected.length &&
                capacityMatches?.length &&
                (rowsSelected.length < capacityMatches?.length ||
                  copyMatchesFromRow)
              }
              checked={checked}
              handleCheckboxClick={() =>
                checked
                  ? dispatch(clearRowsSelected())
                  : dispatch(setAllRowsSelected())
              }
            />
            <HeaderCell
              label={messages.laneId.defaultMessage}
              fieldName="shipperLaneId"
              {...commonHeaderProps("shipperLaneId", "left", true)}
              minWidth={105}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.origCity.defaultMessage}
              fieldName="originCity"
              {...commonHeaderProps("originCity", "left", true)}
              minWidth={110}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.origSt.defaultMessage}
              fieldName="originState"
              {...commonHeaderProps("originState", "left", true)}
              minWidth={100}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.origPostal.defaultMessage}
              fieldName="originPostalCode"
              {...commonHeaderProps("originPostalCode", "left", true)}
              minWidth={125}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.origCountry.defaultMessage}
              fieldName="originCountry"
              {...commonHeaderProps("originCountry", "left", true)}
              minWidth={135}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.destCity.defaultMessage}
              fieldName="destinationCity"
              {...commonHeaderProps("destinationCity", "left", true)}
              minWidth={115}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.destSt.defaultMessage}
              fieldName="destinationState"
              {...commonHeaderProps("destinationState", "left", true)}
              minWidth={100}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.destPostal.defaultMessage}
              fieldName="destinationPostalCode"
              {...commonHeaderProps("destinationPostalCode", "left", true)}
              minWidth={125}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.destCountry.defaultMessage}
              fieldName="destinationCountry"
              {...commonHeaderProps("destinationCountry", "left", true)}
              minWidth={135}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.eq.defaultMessage}
              fieldName="equipmentType"
              {...commonHeaderProps("equipmentType", "left", true)}
              minWidth={85}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.volume.defaultMessage}
              fieldName="volume"
              arrowLeft
              {...commonHeaderProps("volume", "right", true)}
              minWidth={110}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.group.defaultMessage}
              fieldName="groups"
              {...commonHeaderProps("groups", "left", !isPendingOrRejected)}
              minWidth={150}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.procurementReps.defaultMessage}
              fieldName="procurementRepNames"
              {...commonHeaderProps("procurementRepNames", "left", !isPendingOrRejected)}
              minWidth={200}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.carriers.defaultMessage}
              fieldName="carrierNames"
              {...commonHeaderProps(
                "carrierNames",
                "left",
                !isPendingOrRejected
              )}
              minWidth={200}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.carrierReps.defaultMessage}
              fieldName="repNames"
              {...commonHeaderProps("repNames", "left", !isPendingOrRejected)}
              minWidth={200}
              isFilter={true}
              filter={Filter}
            />
            <HeaderCell
              label={messages.incumbent.defaultMessage}
              fieldName="incumbentMatchesCount"
              {...commonHeaderProps(
                "incumbentMatchesCount",
                "left",
                !isPendingOrRejected
              )}
              minWidth={100}
            />
            <HeaderCell
              label={messages.matches.defaultMessage}
              {...commonHeaderProps("matches")}
              resize={false}
              minWidth={350}
              hideLabel={true}
            />
          </>
        )}
      </tr>
    </thead>
  );
};

export default CapacityMatchTableHeader;
